import { useMemo } from 'react';

import { TableLoadingSpinner } from '../../../../design/components/LoadingSpinner';
import { isValidSortDirection } from '../../../pages/talent-directory/sort-option';
import { pageSizeOptions } from '../common/constants';
import { GeneralDataGrid } from '../common/GeneralDataGrid';

import type { CustomAttributes } from '../../../../../../shared/source/data-contract/custom-attribute';
import type { SkillShort } from '../../../../../../shared/source/data-contract/skill';
import type { SortOption } from '../../../pages/talent-directory/sort-option';
import type {
    GridCallbackDetails,
    GridColDef,
    GridColumnVisibilityModel,
    GridPaginationModel,
    GridSlots,
} from '@mui/x-data-grid-pro';
import type { ReactElement } from 'react';

export type LearningOpportunitySearchRow = {
    readonly id: string | undefined;
    readonly createdAt: string | undefined;
    readonly skills: readonly SkillShort[] | undefined;
    readonly title: string | undefined;
};

export type LearningOpportunityRow = {
    readonly id: string | undefined;
    readonly createdAt: string | undefined;
    readonly custom: CustomAttributes | undefined;
    readonly ownerName: string | undefined;
    readonly provider: string | undefined;
    readonly state: string | undefined;
    readonly title: string | undefined;
    readonly updatedAt: string | undefined;
};

type LearningOpportunityGridRow = LearningOpportunityRow | LearningOpportunitySearchRow;

type LearningOpportunitiesDataGridProps<T extends LearningOpportunityGridRow> = {
    // eslint-disable-next-line functional/prefer-readonly-type
    readonly columns: GridColDef<T>[];
    readonly columnVisibilityModel?: GridColumnVisibilityModel;
    readonly isFetching: boolean;
    readonly isPending: boolean;
    readonly noDataMessage: string;
    readonly onColumnVisibilityModelChange?: (model: GridColumnVisibilityModel, details: GridCallbackDetails) => void;
    readonly onPaginationModelChange: (model: GridPaginationModel, details: GridCallbackDetails) => void;
    readonly onRowClick: (value: string) => void;
    readonly onSortOptionsChange: (options: readonly SortOption[]) => void;
    readonly paginationModel: GridPaginationModel;
    readonly rowCount: number;
    readonly rows: readonly T[];
    readonly showToolbar?: boolean;
    readonly slots?: Partial<GridSlots> | undefined;
    readonly sortOptions: readonly SortOption[];
};

export const LearningOpportunitiesDataGrid = <T extends LearningOpportunityGridRow>({
    columns,
    columnVisibilityModel,
    isFetching,
    isPending,
    noDataMessage,
    onColumnVisibilityModelChange,
    onPaginationModelChange,
    onRowClick,
    onSortOptionsChange,
    paginationModel,
    rowCount,
    rows,
    showToolbar,
    slots,
    sortOptions,
}: LearningOpportunitiesDataGridProps<T>): ReactElement<any> => {
    const sortModel = useMemo(
        () =>
            sortOptions.map(({ direction, field }) => ({
                field,
                sort: direction,
            })),
        [sortOptions],
    );

    return (
        <GeneralDataGrid
            columnBufferPx={4}
            columns={columns}
            columnVisibilityModel={columnVisibilityModel}
            disableRowSelectionOnClick={true}
            getRowHeight={() => 'auto'}
            loading={isPending || isFetching}
            localeText={{
                noRowsLabel: noDataMessage,
            }}
            pageSizeOptions={pageSizeOptions}
            pagination={true}
            paginationMode="server"
            paginationModel={paginationModel}
            rowCount={rowCount}
            rows={rows}
            showToolbar={showToolbar}
            slots={{
                loadingOverlay: TableLoadingSpinner,
                ...slots,
            }}
            sortModel={sortModel}
            sx={{
                '& .MuiDataGrid-cell': {
                    alignItems: 'baseline',
                    py: '8px',
                },
                height: '70vh',
            }}
            onColumnVisibilityModelChange={onColumnVisibilityModelChange}
            onPaginationModelChange={onPaginationModelChange}
            onRowClick={(row) => onRowClick(row.id.toString())}
            onSortModelChange={(sortModel) =>
                onSortOptionsChange(
                    sortModel.filter(isValidSortDirection).map(({ field, sort }) => ({ direction: sort, field })),
                )
            }
        />
    );
};
